import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AssignmentSection from './AssignmentSection';
import PasswordPanel from './PasswordPanel';
import TopSection from './TopSection';
import NodeContentFooterSection from '../NodeContentFooterSection';
import useApi from '../../hooks/useApi';

import { PanelWrapper, HeaderSection, Header, EditorWrapper, EditorSection } from './styles';

const HubContentViewer = ({ nodeId, options, panel, fileService, flash, isPasswordLocked }) => {
  const { userToken, circle_uuid, session_id, circle_state: state } = options;

  const { getData, loading } = useApi(userToken);
  const { putData } = useApi(userToken);

  const [data, setData] = useState();
  const [orderedHubs, setOrderedHubs] = useState([]);
  const [navigationItems, setNavigationItems] = useState({});

  const isSession = state === 'session';
  const isTeacher = window.viewer.options.teacherInSession;

  const updateNavigationItems = () => {
    const node = window.viewer.graph.cy.$(`#${nodeId}`);
    setNavigationItems(window.viewer.resolveNavigationItems(node));
  };

  useEffect(() => {
    updateNavigationItems();
  }, []);

  useEffect(() => {
    document.addEventListener('notification.created', handleNotification);
    return () => document.removeEventListener('notification.created', handleNotification);
  }, []);

  const handleNotification = () => {
    updateNavigationItems();
  };

  useEffect(() => {
    const getHubData = async () => {
      if (!loading) {
        const response = await getData(`/api/v3/viewer/circles/${circle_uuid}/hub/${nodeId}`);
        if (response.success) {
          const { hub, orderedHubs } = response.body;
          setData(hub);
          setOrderedHubs(orderedHubs);
        }
      }
    };
    getHubData();
  }, []);

  if (loading || !data) {
    return null;
  }

  const previewUnlock = (code) => {
    if (code === data.code) {
      return { success: true, body: { unlocked: true } };
    } else {
      return { success: false };
    }
  };

  const onContinue = async (code) => {
    const isPreview = typeof PreviewMode !== 'undefined' && window.viewer instanceof window.PreviewMode;
    const response = isPreview
      ? previewUnlock(code)
      : await putData(`/api/v1/sessions/${session_id}/node/${nodeId}/unlock`, { code });
    if (response.success && response.body?.unlocked) {
      document.dispatchEvent(new CustomEvent('node.unlocked', { detail: nodeId }));
      flash(i18n.__('node_unlock_success'), { ttl: 3000, type: 'status' });
    } else {
      flash(i18n.__('node_unlock_failed'), { ttl: 3000 });
    }
  };

  if (isPasswordLocked) {
    return <PasswordPanel panel={panel} loading={loading} name={data.name} onContinue={onContinue} />;
  }

  const isEditorEmpty = (content) => !content || content === '<p><br /></p>';

  return (
    <PanelWrapper className={'panelContent ir-content'}>
      <TopSection panel={panel} options={options} nodeData={data} />
      <HeaderSection>
        <Header className={'ir-title'}>{data.name}</Header>
      </HeaderSection>
      <EditorSection>
        {!isEditorEmpty(data.description) && (
          <EditorWrapper dangerouslySetInnerHTML={{ __html: data.description }} className="content-section" />
        )}
        {!isEditorEmpty(data.inventory) && isTeacher && (
          <EditorWrapper dangerouslySetInnerHTML={{ __html: data.inventory }} className="italic content-section" />
        )}
      </EditorSection>
      <EditorSection>
        <AssignmentSection
          fileService={fileService}
          orderedHubs={orderedHubs}
          navigationItems={navigationItems}
          isSession={isSession}
          nodeId={nodeId}
        />
      </EditorSection>
      <NodeContentFooterSection navigation={navigationItems.navigation} darkMode={true} />
    </PanelWrapper>
  );
};

export default HubContentViewer;

HubContentViewer.propTypes = {
  nodeId: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  isPasswordLocked: PropTypes.bool.isRequired,
};
